<script lang="ts">
import type { InputBlockProps } from '#layers/blocks/types/input-block';
import type { ButtonProps, ColorPreset } from '#layers/ui/public';

export type SemanticColorValue = Exclude<NonNullable<ButtonProps['color']>, 'custom'>;

const SEMANTIC_COLOR_PRESETS: Array<{ value: SemanticColorValue; color: string; label: string; tooltip?: string }> = [
	{ value: 'neutral', color: 'var(--ui-color-neutral-500)', label: '$t:blocks.common.colors.neutral', tooltip: '$t:blocks.common.colors.neutral' },
	{ value: 'primary', color: 'var(--ui-color-primary-500)', label: '$t:blocks.common.colors.primary', tooltip: '$t:blocks.common.colors.primary' },
	{ value: 'success', color: 'var(--ui-color-success-500)', label: '$t:blocks.common.colors.success', tooltip: '$t:blocks.common.colors.success' },
	{ value: 'info', color: 'var(--ui-color-info-500)', label: '$t:blocks.common.colors.info', tooltip: '$t:blocks.common.colors.info' },
	{ value: 'warning', color: 'var(--ui-color-warning-500)', label: '$t:blocks.common.colors.warning', tooltip: '$t:blocks.common.colors.warning' },
	{ value: 'error', color: 'var(--ui-color-error-500)', label: '$t:blocks.common.colors.error', tooltip: '$t:blocks.common.colors.error' },
];

const SEMANTIC_COLOR_VALUES = new Set<SemanticColorValue>(SEMANTIC_COLOR_PRESETS.map((preset) => preset.value));

const SEMANTIC_COLOR_OPTIONS = SEMANTIC_COLOR_PRESETS.map(({ value, label }) => ({ value, label }));

/** Checks whether a string is one of the supported semantic color tokens. */
export function isSemanticColorValue(value: string): value is SemanticColorValue {
	return SEMANTIC_COLOR_VALUES.has(value as SemanticColorValue);
}

interface Props extends InputBlockProps {
	presets?: ColorPreset[];
}

const __DsBlock = {
	icon: 'lucide:palette',
	name: '$t:blocks.input_semantic_color.name',
	description: '$t:blocks.input_semantic_color.description',
	category: 'input',
	tags: ['form-control'],

	options: [
		{
			name: 'modelValue',
			label: '$t:blocks.common.props.value',
		},
		{
			name: 'presets',
			label: '$t:blocks.input_semantic_color.props.presets',
			block: BuiltinBlockIds.InputRepeater,
			props: {
				schema: [
					{
						name: 'value',
						label: '$t:blocks.common.props.value',
						block: BuiltinBlockIds.InputSelect,
						props: {
							options: SEMANTIC_COLOR_OPTIONS,
						},
					},
					{
						name: 'color',
						label: '$t:blocks.common.props.color',
						block: BuiltinBlockIds.InputColor,
						nullable: true,
					},
					{
						name: 'label',
						label: '$t:blocks.common.props.label',
						block: BuiltinBlockIds.Input,
					},
					{
						name: 'tooltip',
						label: '$t:blocks.common.props.tooltip',
						block: BuiltinBlockIds.Input,
					},
				],
				displayBlock: {
					id: '$builtin:DisplayColorPreset',
					props: {},
				},
			},
		},
		{
			name: 'disabled',
			label: '$t:blocks.common.props.disabled',
		},
		{
			name: 'readonly',
			label: '$t:blocks.common.props.readonly',
		},
		{
			name: 'disabledNotice',
			label: '$t:blocks.common.props.disabled_notice',
		},
	],
}</script>

<script lang="ts" setup>
import { BuiltinBlockIds } from 'virtual:monospace-builtin-block-ids';
import { computed } from 'vue';
import { defineBlock } from '#layers/block-engine/public';
import { usePrimitiveFormField } from '#layers/forms/public';
import { useTranslationUtils } from '#layers/i18n/public';
import { useDisabledNotice } from '#layers/ui/public';

const {
	presets = [...SEMANTIC_COLOR_PRESETS],
	readonly,
	disabled: disabledProp,
	disabledNotice: disabledNoticeProp,
} = defineProps<Props>();

;

const model = defineModel<string | null>();
const { modelValue, disabled, disabledNotice } = usePrimitiveFormField(() => ({ disabled: disabledProp, disabledNotice: disabledNoticeProp }), { model });
const { showDisabledNotice } = useDisabledNotice({ disabled, disabledNotice });
const pickerDisabled = computed(() => disabled.value || readonly === true);
const { translateString } = useTranslationUtils();

const pickerModel = computed<string | null>({
	get: () => {
		const value = modelValue.value;

		return value && isSemanticColorValue(value) ? value : null;
	},
	set: (value) => {
		if (!value) {
			modelValue.value = 'neutral';
			return;
		}

		if (isSemanticColorValue(value)) {
			modelValue.value = value;
			return;
		}

		modelValue.value = 'neutral';
	},
});

const translatedPresets = computed<ColorPreset[]>(() => presets.map((preset) => ({
	...preset,
	label: preset.label ? translateString(preset.label) : undefined,
	tooltip: preset.tooltip ? translateString(preset.tooltip) : undefined,
})));
</script>

<template>
	<DTooltip :delay-duration="0" :disabled="!showDisabledNotice" :text="disabledNotice">
		<DColorPresetPicker
			v-model="pickerModel"
			:presets="translatedPresets"
			:aria-readonly="readonly"
			:disabled="pickerDisabled"
		/>
	</DTooltip>
</template>
